import React, { useState, useCallback, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { X, Upload, Loader2, CheckCircle, AlertCircle, Wifi, WifiOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MobileModal, isMobileDevice } from "@/components/mobile";
import { usePWA } from "@/utils/pwa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { trackFormSubmission } from '@/utils/error-tracking/comprehensive-tracking';
import {
  QuickSupportModalProps,
  SupportCategory,
  SupportPriority,
  SupportTemplate,
  SupportRequest,
} from "@/types/support";
import {
  validateSupportForm,
  getCategoryLabel,
  getPriorityLabel,
  getPriorityColor,
  formatFileSize,
  SupportFormData,
} from "@/utils/support/validation";
import { cn } from "@/lib/utils";
import { FileUploadZone } from "@/components/file-attachments/FileUploadZone";
import { FileUploadManager } from "@/utils/file-attachments/upload";
import { FileAttachment } from "@/types/file-attachments";

// Mock templates - in production, these would come from the database
const mockTemplates: SupportTemplate[] = [
  {
    id: "1",
    category: "technical",
    title: "Cannot access student records",
    description: "I'm unable to view or edit student records. The page shows an error or loads indefinitely.",
  },
  {
    id: "2",
    category: "enrollment",
    title: "Student enrollment issue",
    description: "There's an issue with enrolling a student or updating their enrollment status.",
  },
  {
    id: "3",
    category: "reporting",
    title: "Report generation problem",
    description: "Reports are not generating correctly or are missing data.",
  },
];

export const QuickSupportModal: React.FC<QuickSupportModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  userId,
  onSubmit,
  helpContext,
}) => {
  // PWA and mobile functionality
  const { isOnline, storeOfflineRequest } = usePWA();
  const [isMobile, setIsMobile] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const uploadManager = new FileUploadManager();

  // Check if mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<SupportFormData>({
    defaultValues: {
      category: undefined,
      priority: "medium",
      subject: "",
      description: "",
    },
  });

  const selectedCategory = watch("category");

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setAttachments([]);
      setUploadingFiles([]);
      setSubmitStatus("idle");
    } else if (helpContext) {
      // Pre-fill form with help context data
      const contextDescription = `Help Search Query: "${helpContext.searchQuery}"

Viewed Articles: ${helpContext.viewedArticles?.join(', ') || 'None'}

Reason for escalation: ${
        helpContext.escalationReason === 'no_results' ? 'No relevant help articles found' :
        helpContext.escalationReason === 'unhelpful_results' ? 'The help articles were not helpful' :
        'Need personal assistance'
      }

Additional details: `;
      
      setValue('subject', `Help Request: ${helpContext.searchQuery}`);
      setValue('description', contextDescription);
      setValue('category', 'other');
      setValue('priority', 'medium');
    }
  }, [isOpen, reset, helpContext, setValue]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const handleTemplateSelect = (templateId: string) => {
    const template = mockTemplates.find((t) => t.id === templateId);
    if (template) {
      setValue("category", template.category);
      setValue("subject", template.title);
      setValue("description", template.description);
    }
  };

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    if (files.length + attachments.length + uploadingFiles.length > 5) {
      toast({
        title: "Too many files",
        description: "You can upload a maximum of 5 files per support request",
        variant: "destructive",
      });
      return;
    }

    setUploadingFiles(prev => [...prev, ...files]);

    try {
      // Upload files immediately when selected
      const uploadPromises = files.map(async (file) => {
        const result = await uploadManager.uploadFile(file, {
          entity_type: 'support_request',
          access_level: 'school_only'
        });
        
        if (result.success && result.attachment) {
          return result.attachment;
        } else {
          throw new Error(result.error || 'Upload failed');
        }
      });

      const uploadedAttachments = await Promise.all(uploadPromises);
      
      setAttachments(prev => [...prev, ...uploadedAttachments]);
      setUploadingFiles(prev => prev.filter(f => !files.includes(f)));

      toast({
        title: "Files uploaded",
        description: `${uploadedAttachments.length} file(s) uploaded successfully`,
      });

    } catch (error) {
      setUploadingFiles(prev => prev.filter(f => !files.includes(f)));
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload files",
        variant: "destructive",
      });
    }
  }, [attachments.length, uploadingFiles.length, uploadManager]);

  const removeAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const onFormSubmit = async (data: SupportFormData) => {
    // Basic form validation
    const validationErrors = validateSupportForm(data);

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        toast({
          title: "Validation Error",
          description: error.message,
          variant: "destructive",
        });
      });
      return;
    }

    // Check if files are still uploading
    if (uploadingFiles.length > 0) {
      toast({
        title: "Upload in progress",
        description: "Please wait for files to finish uploading",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    const result = await trackFormSubmission(
      async () => {
      const request: Partial<SupportRequest> = {
        schoolId,
        userId,
        category: data.category,
        priority: data.priority,
        subject: data.subject.trim(),
        description: data.description.trim(),
        status: "open",
        attachments: attachments.map(att => att.id), // Pass attachment IDs
      };

      // Handle offline mode
      if (!isOnline) {
        await storeOfflineRequest(request);
        setSubmitStatus("success");
        toast({
          title: "Request saved offline",
          description: "Your support request will be submitted when you're back online.",
        });
      } else {
        // Online submission
        if (onSubmit) {
          await onSubmit(request);
        } else {
          // Mock submission - in production this would save to database
          await new Promise((resolve) => setTimeout(resolve, 1500));
          
          // Update attachments to link them to the support request
          // This would be done server-side in production
        }

        setSubmitStatus("success");
        toast({
          title: "Support request submitted",
          description: "We'll get back to you as soon as possible.",
        });
      }

        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 1500);

        return { success: true };
      },
      {
        formName: 'QuickSupportModal - Submit Support Request',
        showToast: false, // We're handling our own toast messages
        errorMessage: 'Failed to submit support request'
      }
    );

    if (!result.success) {
      setSubmitStatus("error");
      toast({
        title: "Submission failed",
        description: "Please try again or contact support directly.",
        variant: "destructive",
      });
    }
    
    setIsSubmitting(false);
  };

  const availableTemplates = selectedCategory
    ? mockTemplates.filter((t) => t.category === selectedCategory)
    : [];

  const formContent = (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      {/* Connection status indicator for mobile */}
      {isMobile && (
        <div className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
          isOnline 
            ? "bg-green-50 text-green-700 border border-green-200" 
            : "bg-amber-50 text-amber-700 border border-amber-200"
        )}>
          {isOnline ? (
            <>
              <Wifi className="h-4 w-4" />
              <span>Online - Changes will be saved immediately</span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4" />
              <span>Offline - Request will be submitted when connection returns</span>
            </>
          )}
        </div>
      )}

      {/* Category Selection */}
      <div className="space-y-2">
        <Label htmlFor="category">Category *</Label>
        <Controller
          name="category"
          control={control}
          rules={{ required: "Please select a category" }}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger 
                id="category" 
                className={cn(isMobile && "mobile-input")}
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student_management">Student Management</SelectItem>
                <SelectItem value="applications">Applications</SelectItem>
                <SelectItem value="capacity">Capacity</SelectItem>
                <SelectItem value="transportation">Transportation</SelectItem>
                <SelectItem value="reports">Reports</SelectItem>
                <SelectItem value="notifications">Notifications</SelectItem>
                <SelectItem value="portal_sync">Portal Sync</SelectItem>
                <SelectItem value="user_access">User Access</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.category && (
          <p className="text-sm text-red-600">{errors.category.message}</p>
        )}
      </div>

      {/* Template Selection (if available) */}
      {availableTemplates.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="template">Quick Templates</Label>
          <Select onValueChange={handleTemplateSelect}>
            <SelectTrigger 
              id="template"
              className={cn(isMobile && "mobile-input")}
            >
              <SelectValue placeholder="Select a template (optional)" />
            </SelectTrigger>
            <SelectContent>
              {availableTemplates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Priority Selection */}
      <div className="space-y-2">
        <Label htmlFor="priority">Priority *</Label>
        <Controller
          name="priority"
          control={control}
          rules={{ required: "Please select a priority" }}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger 
                id="priority"
                className={cn(isMobile && "mobile-input")}
              >
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="urgent">
                  <span className={getPriorityColor("urgent")}>
                    Urgent - Critical issue affecting operations
                  </span>
                </SelectItem>
                <SelectItem value="high">
                  <span className={getPriorityColor("high")}>
                    High - Important but not blocking
                  </span>
                </SelectItem>
                <SelectItem value="medium">
                  <span className={getPriorityColor("medium")}>
                    Medium - General assistance needed
                  </span>
                </SelectItem>
                <SelectItem value="low">
                  <span className={getPriorityColor("low")}>
                    Low - Minor issue or question
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        {errors.priority && (
          <p className="text-sm text-red-600">{errors.priority.message}</p>
        )}
      </div>

      {/* Subject */}
      <div className="space-y-2">
        <Label htmlFor="subject">Subject *</Label>
        <Input
          id="subject"
          {...register("subject", {
            required: "Subject is required",
            minLength: {
              value: 5,
              message: "Subject must be at least 5 characters",
            },
            maxLength: {
              value: 100,
              message: "Subject must be less than 100 characters",
            },
          })}
          placeholder="Brief summary of your issue"
          className={cn(isMobile && "mobile-input")}
        />
        {errors.subject && (
          <p className="text-sm text-red-600">{errors.subject.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          {...register("description", {
            required: "Description is required",
            minLength: {
              value: 20,
              message: "Please provide more details (at least 20 characters)",
            },
            maxLength: {
              value: 2000,
              message: "Description must be less than 2000 characters",
            },
          })}
          placeholder="Please describe your issue in detail. Include any error messages you're seeing."
          rows={isMobile ? 4 : 5}
          className={cn(isMobile && "mobile-input text-base")}
        />
        {errors.description && (
          <p className="text-sm text-red-600">{errors.description.message}</p>
        )}
      </div>

          {/* File Attachments */}
          <div className="space-y-3">
            <Label>File Attachments (optional)</Label>
            <FileUploadZone
              onFilesSelected={handleFilesSelected}
              maxFiles={5 - attachments.length - uploadingFiles.length}
              disabled={isSubmitting}
              className="border-2 border-dashed border-gray-300 hover:border-gray-400"
            >
              <div className="space-y-3">
                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    Drop files here or click to browse
                  </p>
                  <p className="text-xs text-gray-500">
                    Max 5 files, up to 50MB each. Supports images, PDFs, and documents.
                  </p>
                </div>
              </div>
            </FileUploadZone>

            {/* Uploading Files */}
            {uploadingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Uploading...</p>
                {uploadingFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-2 border border-blue-200 rounded bg-blue-50"
                  >
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm text-blue-900 flex-1 truncate">
                      {file.name} ({formatFileSize(file.size)})
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded Attachments */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Attached Files</p>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center space-x-3 p-2 border border-green-200 rounded bg-green-50"
                  >
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-900 flex-1 truncate">
                      {attachment.original_filename} ({formatFileSize(attachment.file_size)})
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment.id)}
                      className="text-green-700 hover:text-green-900"
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

      {/* Form Actions */}
      <div className={cn(
        "flex gap-2 pt-4",
        isMobile ? "flex-col" : "justify-end"
      )}>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
          className={cn(isMobile && "mobile-button")}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          className={cn(isMobile && "mobile-button")}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isOnline ? "Submitting..." : "Saving..."}
            </>
          ) : submitStatus === "success" ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              {isOnline ? "Submitted" : "Saved Offline"}
            </>
          ) : submitStatus === "error" ? (
            <>
              <AlertCircle className="mr-2 h-4 w-4" />
              Try Again
            </>
          ) : (
            isOnline ? "Submit Request" : "Save for Later"
          )}
        </Button>
      </div>
    </form>
  );

  // Render mobile or desktop modal based on device
  if (isMobile) {
    return (
      <MobileModal
        isOpen={isOpen}
        onClose={onClose}
        title="Get Help"
        subtitle="Describe your issue and we'll help you resolve it quickly."
        fullScreen={false}
        maxHeight="95vh"
        closeOnSwipeDown={true}
        footer={null} // Actions are part of the form
      >
        {formContent}
      </MobileModal>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Get Help</DialogTitle>
          <DialogDescription>
            Describe your issue and we'll help you resolve it quickly.
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};